<template>
  <AuthLayout :mode="mode">
    <Section
      heading="Broadcast-Add (PowerMail)"
      description="Use this page to enter equipment into our database for others to find when they PowerSearch AND to email this info to all dealers who have signed up to receive that manufacturer's listings. We suggest you use our <a href='/members/broadcast-email' class='text-primary-100 font-semibold underline'>PowerCast</a> instead as it let's you send out your Buys/Sells in a free format."
      :mode="mode"
    />
    <AppCard :mode="mode" title="Broadcast Listing Form">
      <template #body>
        <Notes :list="notesList" />
        <div class="mt-6">
          <div class="grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-1 sm:gap-4">
            <AppSelect
              :mode="mode"
              title="Manufacturer"
              v-model="listing.mfr_id"
            >
              <template #options>
                <option
                  v-for="(mfr, key) in mfrs"
                  :key="key"
                  :value="mfr.mfr.mfr_id"
                >
                  {{ mfr.mfr.company}}
                </option>
              </template>
            </AppSelect>
            <AppSelect :mode="mode" title="Buy/Sell" v-model="listing.buy_sell">
              <template #options>
                <option selected value="B">Buy</option>
                <option value="S">Sell</option>
              </template>
            </AppSelect>
            <AppSelect :mode="mode" title="Country" v-model="listing.location">
              <template #options>
                <option value="">Country</option>
                <option
                  v-for="(country, key) in countries"
                  :value="country.code"
                  :key="key"
                >
                  {{ country.country }}
                </option>
              </template>
            </AppSelect>
            <AppInput
              :mode="mode"
              id="date"
              title="Date available/needed by"
              v-model="listing.need_by"
              type="date"
            />
            <div class="col-span-2 sm:col-span-1">
              <AppLabel :mode="mode" htmlFor="subject" title="Subject" />
              <div class="flex">
                <AppInput
                  :mode="mode"
                  v-model="listing.subject"
                  placeholder="Subject"
                />
              </div>
            </div>
            <div class="col-span-2 sm:col-span-1">
              <AppCheckbox
                :mode="mode"
                id="researching"
                title="Researching (Not ready to buy/sell)"
                v-model="listing.researching"
              />
            </div>
          </div>
          <div class="mt-10 md:mt-8">
            <div class="grid gap-2">
              <div class="grid gap-4">
                <div
                  v-for="(item, index) in listing.items"
                  class="flex gap-3 md:grid md:gap-4"
                  :key="index"
                >
                  <div class="flex-1">
                    <div class="grid grid-cols-3 gap-3 md:grid-cols-1 md:gap-4">
                      <div>
                        <AppInput
                          :mode="mode"
                          v-model="item.machine"
                          placeholder="Machine or part number"
                          :hasLabel="false"
                        />
                      </div>
                      <div>
                        <AppInput
                          :mode="mode"
                          v-model="item.model"
                          placeholder="Model or description"
                          :hasLabel="false"
                        />
                      </div>
                      <div>
                        <AppInput
                          :mode="mode"
                          v-model="item.comments"
                          placeholder="Comment"
                          :hasLabel="false"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="flex gap-3 md:grid md:gap-4">
                    <div class="w-[60px] md:w-full">
                      <AppInput
                        :mode="mode"
                        v-model="item.qty"
                        type="number"
                        min="0"
                        classes="text-center md:text-left"
                        placeholder="Qty"
                        :hasLabel="false"
                      />
                    </div>
                    <div class="w-[105px] md:w-full">
                      <div class="relative flex items-center">
                        <AppInput
                          :mode="mode"
                          v-model="item.price"
                          placeholder="Price"
                          maxlength="32"
                          :hasLabel="false"
                        />
                        <span
                          class="absolute right-4 z-10 flex h-6 w-6 items-center justify-center rounded bg-primary-1 text-sm font-medium text-black-100"
                          >$</span
                        >
                      </div>
                    </div>
                    <div class="w-[48px] md:w-full">
                      <span
                        @click="deleteLine(index)"
                        class="flex h-[48px] w-[48px] cursor-pointer items-center justify-center rounded-md border"
                        :class="[
                          mode === 'dark'
                            ? 'border-dark-border text-dark-text'
                            : 'border-border-100 text-black-60',
                          listing.items.length === 1
                            ? 'pointer-events-none opacity-50'
                            : '',
                        ]"
                      >
                        <Trash2 :size="18" :stroke-width="1.75" />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="flex items-center">
                <span
                  @click="addNewLine"
                  class="flex cursor-pointer items-center gap-1 text-[14px] font-medium text-primary-100"
                >
                  <Plus :size="15" :stroke-width="2" class="mt-[1px]" />
                  Add Line
                </span>
              </div>
            </div>
            <div class="mt-6">
              <div class="grid grid-cols-2 gap-3 sm:grid-cols-1 sm:gap-4">
                <AppButton
                  title="Broadcast and add item"
                  @action="addListing"
                />
                <AppButton
                  title="Selective send"
                  @action="selectiveSend"
                  type="secondary"
                />
              </div>
            </div>
          </div>
        </div>
      </template>
    </AppCard>
  </AuthLayout>
</template>

<script>
import Section from "@C/Common/Section.vue";
import { Plus, Trash2 } from "lucide-vue-next";
import ApiRequest from "@L/ApiRequest";
import { useGlobalErrorHandler } from "@L/errorHandler";
import { useUserStore } from "@S/user";
import { useToastStore } from "@S/toast";
import { useLoaderStore } from "@S/loader";
import { useCountriesStore } from "@S/countries";
import Notes from "@C/Common/Notes.vue";
import AppSelect from "@C/Form/AppSelect.vue";
import AppInput from "@C/Form/AppInput.vue";
import AppCard from "@C/Common/AppCard.vue";
import AuthLayout from "@A/AuthLayout.vue";
import AppCheckbox from "@C/Form/AppCheckbox.vue";
import AppButton from "@C/Common/AppButton.vue";
import AppLabel from "@C/Form/AppLabel.vue";

export default {
  name: "AddBroadcast",
  components: {
    AuthLayout,
    Section,
    AppInput,
    AppSelect,
    AppCard,
    Plus,
    Trash2,
    Notes,
    AppCheckbox,
    AppButton,
    AppLabel,
  },
  setup() {
    const userStore = useUserStore();
    const toastStore = useToastStore();
    const loaderStore = useLoaderStore();
    const countriesStore = useCountriesStore();
    const { handleError } = useGlobalErrorHandler();
    return {
      userStore,
      toastStore,
      loaderStore,
      countriesStore,
      handleError,
    };
  },
  props: {
    mode: String,
  },
  data() {
    return {
      mfrs: [],
      listing: {
        mfr_id: "",
        buy_sell: "B",
        location: "",
        mfr: "",
        researching: false,
        need_by: new Date().toISOString().split("T")[0], // Set to today's date
        subject: "",
        items: [
          {
            machine: "",
            model: "",
            comments: "",
            qty: "",
            price: "",
          },
        ],
      },
      mailqueue: {
        recipient_list: "",
      },
      notesList: [
        "There must be information in either the machine or model fields.",
        "The optional date available/needed field is meant to inform fellow traders when the equipment you are selling will be available, or when you need it.",
        "The researching box is to let them know if you are not yet a buyer/seller but are just researching the market.",
      ],
    };
  },
  mounted() {
    this.getMfrs();
    // Ensure countries are loaded
    if (this.countriesStore.countries.length === 0) {
      this.getCountries();
    }
  },
  methods: {
    async getCountries() {
      try {
        const { data } = await ApiRequest().get(`/countries`);
        if (data) {
          this.countriesStore.setCountries(data);
        }
      } catch (e) {
        this.handleError(e);
      }
    },
    async getMfrs() {
      this.loaderStore.setIsLoading(true);
      try {
        const { data } = await ApiRequest().get(`/mfr_summary`);
        if (data) {
          this.mfrs = data
            .filter((item) => item.mfr.company && item.mfr.company.trim() !== "");
            //.sort((a, b) => a.mfr.mfr.localeCompare(b.mfr.mfr));  trying to not sort
          this.listing.mfr_id =
            this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.mfr_id : "";
          this.listing.mfr =
            this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.company : "";
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    async addListing() {
      this.loaderStore.setIsLoading(true);
      const payload = {
        listing: this.listing,
        mailqueue: {
          recipient_list: "ALL" + this.listing.buy_sell + this.listing.mfr_id,
        },
      };
      payload.listing.mfr = this.mfrs.find(
        (mfr) => mfr.mfr.mfr_id == this.listing.mfr_id,
      ).mfr.mfr;
      try {
        if (!this.listing.need_by) {
          this.toastStore.show(
            "Date available/needed by field is required",
            "error",
          );
          return;
        }
        const { data } = await ApiRequest().post(`/listing`, payload);
        if (data) {
          this.toastStore.show(
            "Thank you, your message has been sent",
            "success",
          );
          this.clearInputs();
        }
      } catch (e) {
        this.handleError(e);
      } finally {
        this.loaderStore.setIsLoading(false);
      }
    },
    selectiveSend() {
      try {
        if (!this.listing.need_by) {
          this.toastStore.show(
            "Date available/needed by field is required",
            "error",
          );
          return;
        }
        const data = this.listing;
        data.mfr = this.mfrs.find(
          (mfr) => mfr.mfr.mfr_id == this.listing.mfr_id,
        ).mfr.mfr;
        localStorage.setItem("selective-send-broadcast", JSON.stringify(data));
        this.$router.push({
          name: "selective-broadcast-email",
          query: { type: "broadcast" },
        });
      } catch (e) {
        this.handleError(e);
      }
    },
    clearInputs() {
      this.listing = {
        mfr_id: "",
        buy_sell: "B",
        location: "",
        mfr: "",
        researching: false,
        need_by: new Date().toISOString().split("T")[0], // Set to today's date
        subject: "",
        items: [
          {
            machine: "",
            model: "",
            comments: "",
            qty: "",
            price: "",
          },
        ],
      };
      this.listing.mfr_id =
        this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.mfr_id : "";
      this.listing.mfr =
        this.mfrs && this.mfrs.length > 0 ? this.mfrs[0].mfr.mfr : "";
    },
    addNewLine() {
      if (this.listing.items.length === 10) {
        this.toastStore.show("You reach the limit of lines", "info");
        return;
      }
      this.listing.items.push({
        machine: "",
        model: "",
        comments: "",
        qty: "",
        price: "",
      });
    },
    deleteLine(index) {
      this.listing.items.splice(index, 1);
    },
  },
  computed: {
    countries() {
      return this.countriesStore.countries;
    },
  },
};
</script>
